import { EmailData } from './emailFetcher';

export interface ParsedCustomer {
  name: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    province: string;
    postalCode: string;
  };
}

export interface ParsedOrderItem {
  name: string;
  quantity: number;
}

export interface ParsedEmailOrder {
  customer: ParsedCustomer;
  items: ParsedOrderItem[];
  deliveryRequested: boolean;
  needsReview?: boolean;
  rawEmail: EmailData;
}

/**
 * Product aliases mapping common names to canonical product names
 */
const PRODUCT_ALIASES: { [alias: string]: string } = {
  'moms original': 'Beef & Onion',
  "mom's original": 'Beef & Onion',
  "mom's original beef & onion": 'Beef & Onion',
  original: 'Beef & Onion',
  'beef and onion': 'Beef & Onion',
  traditional: 'Beef & Onion',
  'tradional meat buns': 'Beef & Onion',
  'traditional meat buns': 'Beef & Onion',
  'meat buns': 'Beef & Onion',
  'beef flavour meatbuns': 'Beef & Onion',
  'beef flavor meatbuns': 'Beef & Onion',
  'beef meatbuns': 'Beef & Onion',
  'farmer sausage & cheese': 'Farmer Sausage & Cheese',
  'farmer sausage and cheese': 'Farmer Sausage & Cheese',
  sauerkraut: 'Sauerkraut',
  'sauerkraut no meat': 'Sauerkraut',
  'sauerkraut and bacon': 'Sauerkraut with Bacon',
  'sauerkraut & bacon': 'Sauerkraut with Bacon',
  'sauerkaraut and bacon': 'Sauerkraut with Bacon',
  'spinach & feta': 'Spinach & Feta',
  'spinach and feta': 'Spinach & Feta',
  'spinach and feta no meat': 'Spinach & Feta',
  'spinach feta': 'Spinach & Feta',
  chickpea: 'Chickpea',
  'chickpeas & sundried tomato': 'Chickpea'
};

/**
 * Calculate string similarity using Levenshtein distance
 */
const calculateSimilarity = (str1: string, str2: string): number => {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();

  const len1 = s1.length;
  const len2 = s2.length;

  const matrix: number[][] = [];

  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(matrix[i - 1][j] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j - 1] + cost);
    }
  }

  const maxLen = Math.max(len1, len2);
  if (maxLen === 0) return 1;

  return 1 - matrix[len1][len2] / maxLen;
};

/**
 * Fuzzy match a product name against a list of known products
 * Returns the best match if similarity is above threshold
 * First checks product aliases for exact matches before falling back to fuzzy matching
 */
export const fuzzyMatchProduct = (
  productText: string,
  availableProducts: Array<{ id: string; name: string }>,
  threshold: number = 0.6
): { id: string; name: string; similarity: number } | null => {
  if (!productText || availableProducts.length === 0) {
    return null;
  }

  const normalizedText = productText.toLowerCase().trim();

  // Check for exact alias match first
  const canonicalName = PRODUCT_ALIASES[normalizedText];
  if (canonicalName) {
    // Find the product with this canonical name
    const matchedProduct = availableProducts.find(p => p.name === canonicalName);
    if (matchedProduct) {
      return { ...matchedProduct, similarity: 1.0 };
    }
  }

  // Fall back to fuzzy matching
  let bestMatch: { id: string; name: string; similarity: number } | null = null;
  let bestSimilarity = 0;

  for (const product of availableProducts) {
    const similarity = calculateSimilarity(productText, product.name);
    if (similarity > bestSimilarity) {
      bestSimilarity = similarity;
      bestMatch = { ...product, similarity };
    }
  }

  if (bestMatch && bestSimilarity >= threshold) {
    return bestMatch;
  }

  return null;
};

/**
 * Check if a line is conversational noise (greetings, thanks, questions, delivery requests)
 */
const isConversationalNoise = (line: string): boolean => {
  const lower = line.toLowerCase().trim();

  // Empty lines
  if (!lower) return true;

  // Greetings
  if (/^(hi|hello|hey|dear|good morning|good afternoon|good evening)/.test(lower)) {
    return true;
  }

  // Thanks/pleasantries
  if (/^(thanks|thank you|please|regards|best|cheers|sincerely)/.test(lower)) {
    return true;
  }

  // Lines containing "please"
  if (/\bplease\b/i.test(lower)) {
    return true;
  }

  // Conditional phrases
  if (/\bif so\b|\bif possible\b|\bif you (can|could|have)\b/i.test(lower)) {
    return true;
  }

  // Questions (lines ending with ?)
  if (lower.endsWith('?')) {
    return true;
  }

  // Delivery requests
  if (/delivery|deliver|drop off|pick.*up|pickup/i.test(lower)) {
    return true;
  }

  // Common email closing patterns
  if (/^(sent from|get outlook|this email)/i.test(lower)) {
    return true;
  }

  return false;
};

/**
 * Extract phone number from text
 */
const extractPhoneNumber = (text: string): string | null => {
  // Match patterns like: (123) 456-7890, 123-456-7890, 123.456.7890, 1234567890
  const phonePattern = /(\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4})/;
  const match = text.match(phonePattern);
  return match ? match[1] : null;
};

/**
 * Extract email address from text
 */
const extractEmailAddress = (text: string): string | null => {
  const emailPattern = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/;
  const match = text.match(emailPattern);
  return match ? match[1] : null;
};

/**
 * Check if a line looks like a person's name
 */
const looksLikeName = (line: string): boolean => {
  // Names typically:
  // - Start with capital letters (but allow lowercase particles like "de la", "von")
  // - Are not too long (< 50 chars)
  // - Don't contain special characters (except spaces, hyphens, apostrophes)
  // - Allow numbers at end only (for suffixes like "Jr.", "3rd")
  // - Allow single names (e.g., "Madonna")
  if (!line || line.length > 50 || line.length < 3) return false;

  // Allow numbers only at end (e.g., "John Smith Jr.", "Smith 3rd")
  // Reject if numbers appear at start or in middle positions
  if (/^\d/.test(line) || /\d.*[a-zA-Z].*\d/.test(line)) return false;

  // Allow lowercase starts for particles like "von", "del", "de la"
  // But reject all-lowercase (likely not a name)
  if (line === line.toLowerCase()) return false;

  if (!/^[A-Za-z\s'-]+$/.test(line)) return false;

  // Don't match common header words
  const lower = line.toLowerCase();
  if (
    lower === 'your order' ||
    lower === 'order details' ||
    lower === 'name' ||
    lower === 'email' ||
    lower === 'phone' ||
    lower === 'address'
  ) {
    return false;
  }

  return true;
};

/**
 * Parse email signature to extract customer info
 * Looks for patterns like:
 *   Name
 *   Phone: (123) 456-7890
 *   Address line
 */
const parseEmailSignature = (body: string): Partial<ParsedCustomer> => {
  const lines = body
    .split('\n')
    .map(line => line.trim())
    .filter(line => line);
  const info: Partial<ParsedCustomer> = {};

  let foundPhoneIndex = -1;
  let foundAddressIndex = -1;

  // Look for signature patterns in the email body
  // Typically found after order content
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lowerLine = line.toLowerCase();

    // Extract phone if found
    if (!info.phone) {
      const phone = extractPhoneNumber(line);
      if (phone) {
        info.phone = phone;
        foundPhoneIndex = i;
      }
    }

    // Extract email if found (and not a common system email)
    if (!info.email) {
      const email = extractEmailAddress(line);
      if (email && !email.includes('noreply') && !email.includes('no-reply')) {
        info.email = email;
      }
    }

    // Look for address patterns
    // Canadian postal code pattern: A1A 1A1 or A1A1A1
    const postalCodeMatch = line.match(/([A-Z]\d[A-Z]\s?\d[A-Z]\d)/i);
    if (postalCodeMatch && !info.address) {
      foundAddressIndex = i;

      // Parse the line containing address components
      const addressParts = parseAddressLine(line);

      // If street was not in the same line, look backward for it
      let street = addressParts.street;
      if (!street) {
        for (let j = i - 1; j >= Math.max(0, i - 3); j--) {
          const prevLine = lines[j].trim();
          const lowerPrevLine = prevLine.toLowerCase();
          if (
            prevLine &&
            !isConversationalNoise(prevLine) &&
            !extractPhoneNumber(prevLine) &&
            !extractEmailAddress(prevLine) &&
            !looksLikeName(prevLine) &&
            // Filter out order-related lines
            !lowerPrevLine.includes('your order') &&
            !lowerPrevLine.includes('order details') &&
            !/quantity\s+\d+/i.test(prevLine) &&
            !/\d+\s+(dozen|doz|bags?)/i.test(prevLine) &&
            !/^\d+\./.test(prevLine) // Filter numbered lists
          ) {
            street = prevLine;
            break;
          }
        }
      }

      info.address = {
        street: street,
        city: addressParts.city,
        province: addressParts.province,
        postalCode: addressParts.postalCode
      };
    }
  }

  // Try to find name: typically appears right before phone or at the start of signature block
  if (!info.name) {
    // Look for a line that looks like a name near the phone number or address
    const searchStart = Math.max(0, Math.min(foundPhoneIndex, foundAddressIndex) - 3);
    const searchEnd =
      foundPhoneIndex >= 0 ? foundPhoneIndex : foundAddressIndex >= 0 ? foundAddressIndex : lines.length;

    for (let i = searchStart; i < searchEnd; i++) {
      if (looksLikeName(lines[i])) {
        info.name = lines[i];
        break;
      }
    }
  }

  return info;
};

/**
 * Extracts customer information from email body using text pattern matching
 * Looks for "Name\n\n{value}\n\nEmail" pattern or email signatures
 */
const extractCustomerInfo = (body: string, replyTo: string): ParsedCustomer => {
  const lines = body.split('\n').map(line => line.trim());

  const customer: ParsedCustomer = {
    name: '',
    email: replyTo,
    phone: '',
    address: {
      street: '',
      city: '',
      province: '',
      postalCode: ''
    }
  };

  // Find indices of field labels
  const nameIndex = lines.findIndex(line => line.toLowerCase() === 'name');
  const emailIndex = lines.findIndex(line => line.toLowerCase() === 'email');
  const phoneIndex = lines.findIndex(line => line.toLowerCase() === 'phone');
  const addressIndex = lines.findIndex(line => line.toLowerCase() === 'address');

  // Extract name (value between "Name" and "Email" labels)
  if (nameIndex !== -1) {
    // Look for non-empty line after "Name" label
    for (let i = nameIndex + 1; i < lines.length; i++) {
      if (lines[i] && lines[i] !== '' && i < (emailIndex !== -1 ? emailIndex : lines.length)) {
        customer.name = lines[i];
        break;
      }
    }
  }

  // Extract email (value after "Email" label, or use replyTo as fallback)
  if (emailIndex !== -1) {
    for (let i = emailIndex + 1; i < lines.length; i++) {
      if (lines[i] && lines[i] !== '' && i < (phoneIndex !== -1 ? phoneIndex : lines.length)) {
        customer.email = lines[i];
        break;
      }
    }
  }

  // Extract phone (value after "Phone" label)
  if (phoneIndex !== -1) {
    for (let i = phoneIndex + 1; i < lines.length; i++) {
      if (lines[i] && lines[i] !== '' && i < (addressIndex !== -1 ? addressIndex : lines.length)) {
        customer.phone = lines[i];
        break;
      }
    }
  }

  // Extract address (multi-line value after "Address" label)
  if (addressIndex !== -1) {
    const addressLines: string[] = [];
    for (let i = addressIndex + 1; i < lines.length; i++) {
      if (lines[i] && lines[i] !== '') {
        // Stop if we hit another known label
        const lowerLine = lines[i].toLowerCase().trim();
        if (
          lowerLine === 'name' ||
          lowerLine === 'email' ||
          lowerLine === 'phone' ||
          lowerLine === 'your order' ||
          lowerLine === 'order details' ||
          lowerLine === 'canada' ||
          lowerLine.startsWith('map it') ||
          lowerLine.includes('map it')
        ) {
          break;
        }
        // Stop if line contains a Canadian postal code (we've reached the end of the address)
        if (/[A-Z]\d[A-Z]\s*\d[A-Z]\d/i.test(lines[i])) {
          addressLines.push(lines[i]);
          break;
        }
        addressLines.push(lines[i]);
      } else if (addressLines.length > 0) {
        // Stop at empty line after we've collected some address lines
        break;
      }
    }

    // Parse address lines into components
    if (addressLines.length > 0) {
      // Last line usually contains city, province, postal code (and possibly street)
      const lastLine = addressLines[addressLines.length - 1];
      const addressParts = parseAddressLine(lastLine);

      // Use street from parsed result if available, otherwise use first address line
      customer.address.street = addressParts.street || addressLines[0] || '';
      customer.address.city = addressParts.city;
      customer.address.province = addressParts.province;
      customer.address.postalCode = addressParts.postalCode;
    }
  }

  // If form fields didn't work, try parsing email signature
  const signatureInfo = parseEmailSignature(body);

  // Use signature info as fallback for missing fields
  if (!customer.name && signatureInfo.name) {
    customer.name = signatureInfo.name;
  }
  if (!customer.phone && signatureInfo.phone) {
    customer.phone = signatureInfo.phone;
  }
  if (!customer.email || customer.email === replyTo) {
    if (signatureInfo.email) {
      customer.email = signatureInfo.email;
    }
  }
  if (!customer.address.street && signatureInfo.address) {
    customer.address = signatureInfo.address;
  }

  return customer;
};

/**
 * Parse a city/province/postal code line
 * Expected format: "Toronto, ON M5V 3A8" or "Winnipeg, Manitoba R3T0C8" or "50 Wildwood Park, Winnipeg, Manitoba R3T0C8"
 */
const parseAddressLine = (line: string): { street: string; city: string; province: string; postalCode: string } => {
  const result = {
    street: '',
    city: '',
    province: '',
    postalCode: ''
  };

  // Extract postal code first
  const postalCodeMatch = line.match(/([A-Z]\d[A-Z]\s*\d[A-Z]\d)/i);
  if (!postalCodeMatch) {
    result.city = line;
    return result;
  }

  result.postalCode = postalCodeMatch[1]
    .trim()
    .toUpperCase()
    .replace(/(\w{3})(\w{3})/, '$1 $2');

  // Remove postal code from line
  const withoutPostalCode = line.substring(0, postalCodeMatch.index).trim();

  // Split by commas to get parts
  const parts = withoutPostalCode.split(',').map(p => p.trim());

  if (parts.length >= 3) {
    // Format: Street, City, Province
    result.street = parts[0];
    result.city = parts[1];
    const provinceText = parts[2].toUpperCase();

    // Map full province names to abbreviations
    const provinceMap: { [key: string]: string } = {
      ONTARIO: 'ON',
      QUEBEC: 'QC',
      'NOVA SCOTIA': 'NS',
      'NEW BRUNSWICK': 'NB',
      MANITOBA: 'MB',
      'BRITISH COLUMBIA': 'BC',
      'PRINCE EDWARD ISLAND': 'PE',
      SASKATCHEWAN: 'SK',
      ALBERTA: 'AB',
      'NEWFOUNDLAND AND LABRADOR': 'NL'
    };

    result.province = provinceMap[provinceText] || provinceText.substring(0, 2).toUpperCase();
  } else if (parts.length === 2) {
    // Format: City, Province
    result.city = parts[0];
    const provinceText = parts[1].toUpperCase();

    const provinceMap: { [key: string]: string } = {
      ONTARIO: 'ON',
      QUEBEC: 'QC',
      'NOVA SCOTIA': 'NS',
      'NEW BRUNSWICK': 'NB',
      MANITOBA: 'MB',
      'BRITISH COLUMBIA': 'BC',
      'PRINCE EDWARD ISLAND': 'PE',
      SASKATCHEWAN: 'SK',
      ALBERTA: 'AB',
      'NEWFOUNDLAND AND LABRADOR': 'NL'
    };

    result.province = provinceMap[provinceText] || provinceText.substring(0, 2).toUpperCase();
  } else {
    result.city = parts[0] || '';
  }

  return result;
};

/**
 * Normalize quantity strings to numeric values
 * Handles formats: "x3", "3 dozen", "2 doz.", "quantity 6", "3", "1 bag", "2 units" -> converts to numeric quantities
 * Default unit is dozen (1 bag = 1 dozen, 1 unit = 1 dozen)
 */
const normalizeQuantity = (quantityStr: string): number => {
  const str = quantityStr.toLowerCase().trim();

  // Pattern 1: "quantity 6" or "qty 6" - extract the number, ignore the "quantity" prefix
  let match = str.match(/(?:quantity|qty)\s+(\d+)/);
  if (match) {
    return parseInt(match[1], 10);
  }

  // Pattern 2: "x3" or "3x"
  match = str.match(/x\s*(\d+)|(\d+)\s*x/);
  if (match) {
    return parseInt(match[1] || match[2], 10);
  }

  // Pattern 3: "3 dozen" or "3 doz." or "3 doz"
  match = str.match(/(\d+)\s*doz(en|\.)?/);
  if (match) {
    return parseInt(match[1], 10);
  }

  // Pattern 4: "1 bag" or "2 bags" (1 bag = 1 dozen)
  match = str.match(/(\d+)\s*bags?/);
  if (match) {
    return parseInt(match[1], 10);
  }

  // Pattern 5: "1 unit" or "2 units" (1 unit = 1 dozen)
  match = str.match(/(\d+)\s*units?/);
  if (match) {
    return parseInt(match[1], 10);
  }

  // Pattern 6: just a number
  match = str.match(/(\d+)/);
  if (match) {
    return parseInt(match[1], 10);
  }

  // Default to 1 if we can't parse
  return 1;
};

/**
 * Extract order items from "Your Order" section or free-form email body
 * Handles various quantity formats and filters out conversational noise
 * If availableProducts is provided, only items that fuzzy match against known products will be included
 */
const extractOrderItems = (
  body: string,
  availableProducts?: Array<{ id: string; name: string }>
): { items: ParsedOrderItem[]; hasUnmatchedItems: boolean } => {
  const items: ParsedOrderItem[] = [];
  const lines = body.split('\n').map(line => line.trim());

  // Find the "Your Order" section
  let orderSectionIndex = lines.findIndex(
    line => line.toLowerCase().includes('your order') || line.toLowerCase().includes('order details')
  );

  // If no "Your Order" section, try to parse the whole email body
  if (orderSectionIndex === -1) {
    orderSectionIndex = 0;
  } else {
    // Skip the "Your Order" header itself
    orderSectionIndex++;
  }

  // Parse items after "Your Order" header or from start of email
  let emptyLineCount = 0;
  for (let i = orderSectionIndex; i < lines.length; i++) {
    const line = lines[i];

    // Skip empty lines but stop if we hit too many consecutive empty lines (3+)
    if (!line) {
      emptyLineCount++;
      if (emptyLineCount >= 3) {
        break;
      }
      continue;
    }

    // Reset empty line count when we find a non-empty line
    emptyLineCount = 0;

    // Filter out conversational noise
    if (isConversationalNoise(line)) {
      continue;
    }

    // Stop at next major section or signature
    if (line.toLowerCase().includes('total') || line.toLowerCase().includes('thank you')) {
      break;
    }

    // Stop if we hit what looks like a signature (name followed by phone or address)
    if (looksLikeName(line)) {
      // Check if next line has phone or address
      if (i + 1 < lines.length) {
        const nextLine = lines[i + 1];
        const hasPhone = extractPhoneNumber(nextLine);
        const hasPostalCode = /[A-Z]\d[A-Z]\s?\d[A-Z]\d/i.test(nextLine);

        if (hasPhone || hasPostalCode) {
          // Only stop if we've already parsed at least one item
          // This prevents stopping too early if signature appears before items
          if (items.length > 0) {
            break;
          }
        }
      }
    }

    // Try to parse item line
    // Common formats:
    // - "3 dozen beef and onion"
    // - "2 doz original"
    // - "quantity 6 moms original"
    // - "1 beef and onion"
    // - "Farmer Sausage & Sage -2"
    // - "Chocolate Chip Cookies x3"
    // - "Chocolate Chip Cookies - 3 dozen"
    // - "3x Chocolate Chip Cookies"
    // - "Chocolate Chip Cookies (3)"

    let itemName = '';
    let quantity = 1;

    // Pattern 1: "Item Name - 3" or "Item Name -2" (quantity right after dash)
    let match = line.match(/^(.+?)\s*[-–—]\s*(\d+)$/);
    if (match) {
      itemName = match[1].trim();
      quantity = parseInt(match[2], 10);
    } else {
      // Pattern 2: "Item Name - 3 dozen" or "Item Name - x3"
      match = line.match(/^(.+?)\s+[-–—]\s*(.+)$/);
      if (match) {
        itemName = match[1].trim();
        quantity = normalizeQuantity(match[2]);
      } else {
        // Pattern 3: "3 dozen Item Name" or "2 doz Item Name" or "3 bags Item Name" or "2 units Item Name"
        match = line.match(/^(\d+\s*(?:dozen|doz\.?|bags?|units?|x))\s+(.+)$/i);
        if (match) {
          quantity = normalizeQuantity(match[1]);
          itemName = match[2].trim();
        } else {
          // Pattern 4: "quantity 6 Item Name" or "qty 6 Item Name"
          match = line.match(/^(?:quantity|qty)\s+(\d+)\s+(.+)$/i);
          if (match) {
            quantity = parseInt(match[1], 10);
            itemName = match[2].trim();
          } else {
            // Pattern 5: "Item Name x3" (no dash)
            match = line.match(/^(.+?)\s+(x\d+|\d+\s*x|\d+\s*doz(?:en|\.)?|\d+\s*dozen|quantity\s+\d+)$/i);
            if (match) {
              itemName = match[1].trim();
              quantity = normalizeQuantity(match[2]);
            } else {
              // Pattern 6: "3x Item Name"
              match = line.match(/^(x?\d+\s*x?|\d+\s*doz(?:en|\.)?)\s+(.+)$/i);
              if (match) {
                quantity = normalizeQuantity(match[1]);
                itemName = match[2].trim();
              } else {
                // Pattern 7: "Item Name (3)"
                match = line.match(/^(.+?)\s*\((\d+)\)$/);
                if (match) {
                  itemName = match[1].trim();
                  quantity = parseInt(match[2], 10);
                } else {
                  // Pattern 8: "3 Item Name" (just number and item)
                  match = line.match(/^(\d+)\s+(.+)$/);
                  if (match) {
                    // Check if the item name makes sense (not too short, not just numbers)
                    const potentialName = match[2].trim();
                    if (potentialName.length > 2 && !/^\d+$/.test(potentialName)) {
                      quantity = parseInt(match[1], 10);
                      itemName = potentialName;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    // If we still haven't parsed it and the line looks like it could be an item
    // (contains letters and is not too short), treat it as quantity 1
    if (
      !itemName &&
      line.length > 2 &&
      /[a-zA-Z]/.test(line) &&
      !/^(name|email|phone|address|from|to|subject):/i.test(line)
    ) {
      itemName = line;
      quantity = 1;
    }

    if (itemName) {
      items.push({
        name: itemName,
        quantity
      });
    }
  }

  // If availableProducts is provided, filter items to only include those that match known products
  if (availableProducts && availableProducts.length > 0) {
    const matchedItems: ParsedOrderItem[] = [];
    let hasUnmatchedItems = false;

    for (const item of items) {
      const match = fuzzyMatchProduct(item.name, availableProducts);
      if (match) {
        // Use the matched product name instead of the original parsed text
        matchedItems.push({
          name: match.name,
          quantity: item.quantity
        });
      } else {
        // Track that we found an item that couldn't be matched to a known recipe
        hasUnmatchedItems = true;
      }
    }

    return { items: matchedItems, hasUnmatchedItems };
  }

  return { items, hasUnmatchedItems: false };
};

/**
 * Detect if delivery is requested in the email
 */
const detectDeliveryRequest = (body: string): boolean => {
  const lowerBody = body.toLowerCase();
  return /\b(delivery|deliver|drop off|drop-off)\b/i.test(lowerBody);
};

/**
 * Extract the original "Your Order" section text from email for reference
 * Returns the content between "Your Order" header and the end of the items section
 */
export const extractOriginalOrderSection = (body: string): string => {
  const lines = body.split('\n').map(line => line.trim());

  // Find the "Your Order" section
  const orderSectionIndex = lines.findIndex(
    line => line.toLowerCase().includes('your order') || line.toLowerCase().includes('order details')
  );

  if (orderSectionIndex === -1) {
    // No "Your Order" section found, return empty string
    return '';
  }

  // Collect lines from the "Your Order" header onwards
  const orderLines: string[] = [];
  let emptyLineCount = 0;

  for (let i = orderSectionIndex; i < lines.length; i++) {
    const line = lines[i];

    // Stop if we hit too many consecutive empty lines (3+)
    if (!line) {
      emptyLineCount++;
      if (emptyLineCount >= 3) {
        break;
      }
      orderLines.push(line);
      continue;
    }

    // Reset empty line count
    emptyLineCount = 0;

    // Stop at signature indicators
    if (looksLikeName(line)) {
      // Check if next line has phone or address (signature pattern)
      if (i + 1 < lines.length) {
        const nextLine = lines[i + 1];
        if (extractPhoneNumber(nextLine) || /[A-Z]\d[A-Z]\s?\d[A-Z]\d/i.test(nextLine)) {
          break;
        }
      }
    }

    // Stop at common section endings
    const lowerLine = line.toLowerCase();
    if (lowerLine.includes('total') || lowerLine.includes('thank you') || lowerLine.includes('thanks')) {
      break;
    }

    orderLines.push(line);
  }

  return orderLines.join('\n').trim();
};

/**
 * Check if email body has structured format markers
 */
const hasStructuredFormat = (body: string): boolean => {
  const bodyLower = body.toLowerCase();
  const markers = ['your order', 'name', 'email', 'phone', 'address', 'canada'];
  return markers.every(marker => bodyLower.includes(marker));
};

/**
 * Parse email body to extract order items and customer contact information
 *
 * @param emailData - Raw email data from email fetcher
 * @param availableProducts - Optional list of available products to filter against
 * @returns Structured order data with customer info, items, and delivery preference
 */
export const parseOrderEmail = (
  emailData: EmailData,
  availableProducts?: Array<{ id: string; name: string }>
): ParsedEmailOrder => {
  // Try structured parsing first if markers are present
  if (hasStructuredFormat(emailData.body)) {
    try {
      // Import the structured parser dynamically
      const structuredParser = require('./orderParser');
      const parsed = structuredParser.parseOrderEmail(emailData.body, availableProducts || []);

      // Convert to ParsedEmailOrder format
      // Only use structured parser result if name was successfully extracted
      const hasValidName = Boolean(parsed.customer.name && parsed.customer.name.trim().length > 0);
      const hasItemsOrNoErrors = parsed.errors.length === 0 || parsed.items.length > 0;

      if (hasItemsOrNoErrors && hasValidName) {
        console.log('[emailParser] Using structured parser result - name:', parsed.customer.name);

        // Check if any items don't have a matched recipe
        const hasUnmatchedItems = parsed.items.some(
          (item: { matchedRecipe?: { id: string; name: string; similarity: number } }) => !item.matchedRecipe
        );

        return {
          customer: {
            name: parsed.customer.name,
            email: parsed.customer.email,
            phone: parsed.customer.phone,
            address: {
              street: parsed.customer.streetAddress,
              city: parsed.customer.city,
              province: parsed.customer.province,
              postalCode: parsed.customer.postalCode
            }
          },
          items: parsed.items.map(
            (item: {
              matchedRecipe?: { id: string; name: string; similarity: number };
              recipeName: string;
              quantity: number;
            }) => ({
              name: item.matchedRecipe?.name || item.recipeName,
              quantity: item.quantity
            })
          ),
          deliveryRequested: parsed.deliveryType === 'Delivery',
          needsReview: hasUnmatchedItems,
          rawEmail: emailData
        };
      } else {
        console.log('[emailParser] Structured parser validation failed:', {
          hasName: !!parsed.customer.name,
          nameTrimmed: parsed.customer.name?.trim() || '(empty)',
          nameLength: parsed.customer.name?.length || 0,
          itemsCount: parsed.items.length,
          errorsCount: parsed.errors.length,
          errors: parsed.errors
        });
        console.log('[emailParser] Falling back to flexible parser');
      }
    } catch (error) {
      console.log('[emailParser] Structured parsing failed, falling back to flexible parser:', error);
    }
  }

  // Fall back to flexible parsing
  const customer = extractCustomerInfo(emailData.body, emailData.replyTo);
  const { items, hasUnmatchedItems } = extractOrderItems(emailData.body, availableProducts);
  const deliveryRequested = detectDeliveryRequest(emailData.body);

  return {
    customer,
    items,
    deliveryRequested,
    needsReview: hasUnmatchedItems,
    rawEmail: emailData
  };
};

/**
 * Parse multiple order emails
 *
 * @param emails - Array of raw email data
 * @param availableProducts - Optional list of available products to filter against
 * @returns Array of parsed order data
 */
export const parseOrderEmails = (
  emails: EmailData[],
  availableProducts?: Array<{ id: string; name: string }>
): ParsedEmailOrder[] => {
  return emails.map(email => parseOrderEmail(email, availableProducts));
};
